import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { 
  Building, Search, Loader2, MapPin, Globe, ChevronRight, ExternalLink,
  Pill, FlaskConical, Handshake, Users, TrendingUp, Mail, Linkedin,
  Sparkles, Copy, Check, DollarSign, Shield, AlertTriangle, FileText, Zap, Lock, Lightbulb
} from "lucide-react";
import AppLayout from "@/components/AppLayout";
import PaywallModal from "@/components/PaywallModal";
import ContributionModal from "@/components/ContributionModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getScoreColor = (score) => {
  if (score >= 80) return "bg-green-600 text-white";
  if (score >= 60) return "bg-teal-600 text-white";
  if (score >= 40) return "bg-amber-500 text-white";
  return "bg-slate-400 text-white";
};

const getScoreBg = (score) => {
  if (score >= 80) return "border-green-200 bg-green-50";
  if (score >= 60) return "border-teal-200 bg-teal-50";
  if (score >= 40) return "border-amber-200 bg-amber-50";
  return "border-slate-200 bg-slate-50";
};

const CONTACT_APPROACH_LABELS = {
  bd_team: "BD Team",
  scientists: "Scientists",
  both: "BD Team + Scientists",
  licensing_team: "Licensing Team"
};

function PharmaCard({ pharma, onSelect }) {
  return (
    <Card 
      className={`cursor-pointer transition-all hover:shadow-lg border-2 ${getScoreBg(pharma.match_score)}`}
      onClick={() => onSelect(pharma)}
      data-testid={`pharma-card-${pharma.pharma_id}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0">
              <Building className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">{pharma.name}</h3>
              <div className="text-sm text-slate-500 flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" />
                {pharma.headquarters || "Global"}
              </div>
            </div>
          </div>
          <Badge className={`${getScoreColor(pharma.match_score)} px-3 py-1`}>
            {pharma.match_score}%
          </Badge>
        </div>

        {/* Match reasons */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {pharma.match_reasons?.slice(0, 3).map((reason, i) => (
            <Badge key={i} variant="secondary" className="text-xs">{reason}</Badge>
          ))}
        </div>

        {/* Key info */}
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-slate-600">
            <FlaskConical className="w-4 h-4 text-indigo-500" />
            <span className="line-clamp-1">{pharma.indication_focus?.slice(0, 3).join(", ")}</span>
          </div>
          {pharma.key_drugs?.length > 0 && (
            <div className="flex items-center gap-2 text-slate-600">
              <Pill className="w-4 h-4 text-purple-500" />
              <span className="line-clamp-1">{pharma.key_drugs?.slice(0, 3).join(", ")}</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-slate-600">
            <Handshake className="w-4 h-4 text-teal-500" />
            <span>Contact via: {CONTACT_APPROACH_LABELS[pharma.best_contact_approach] || "BD Team"}</span>
          </div>
        </div>

        <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-200">
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <TrendingUp className="w-3 h-3" />
              {pharma.external_innovation_score || 50}% external
            </span>
          </div>
          <Button variant="ghost" size="sm" className="text-indigo-600 gap-1">
            View Details <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function PharmaDetailDialog({ pharma, open, onClose }) {
  const [activeTab, setActiveTab] = useState("overview");
  const [generatingMessage, setGeneratingMessage] = useState(false);
  const [outreachMessage, setOutreachMessage] = useState("");
  const [copied, setCopied] = useState(false);

  if (!pharma) return null;

  const generateOutreach = async (contactType) => {
    setGeneratingMessage(true);
    setOutreachMessage("");
    try {
      const response = await fetch(`${BACKEND_URL}/api/pharma/${pharma.pharma_id}/outreach`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ contact_type: contactType })
      });
      if (response.ok) {
        const data = await response.json();
        setOutreachMessage(data.message);
      } else {
        toast.error("Failed to generate message");
      }
    } catch {
      toast.error("Failed to generate message");
    } finally {
      setGeneratingMessage(false);
    }
  };

  const copyMessage = () => {
    navigator.clipboard.writeText(outreachMessage);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Copied to clipboard");
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Building className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-xl">{pharma.name}</DialogTitle>
              <DialogDescription className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {pharma.headquarters || "Global"}
              </DialogDescription>
            </div>
            <Badge className={`${getScoreColor(pharma.match_score)} px-4 py-2 text-lg`}>
              {pharma.match_score}%
            </Badge>
          </div>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="partnerships">Partnerships</TabsTrigger>
            <TabsTrigger value="contact">How to Contact</TabsTrigger>
            <TabsTrigger value="outreach">Draft Message</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4 pt-4">
            {pharma.description && (
              <p className="text-slate-600">{pharma.description}</p>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="text-sm text-slate-500 mb-1">R&D Spend</div>
                <div className="font-semibold text-slate-900 flex items-center gap-1">
                  <DollarSign className="w-4 h-4" />
                  {pharma.annual_rd_spend || "N/A"}
                </div>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="text-sm text-slate-500 mb-1">External Innovation</div>
                <div className="font-semibold text-slate-900">{pharma.external_innovation_score || 50}%</div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                <FlaskConical className="w-4 h-4" />
                Therapeutic Focus
              </h4>
              <div className="flex flex-wrap gap-2">
                {pharma.indication_focus?.map((ind, i) => (
                  <Badge key={i} variant="secondary">{ind}</Badge>
                ))}
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Modality Focus
              </h4>
              <div className="flex flex-wrap gap-2">
                {pharma.modality_focus?.map((mod, i) => (
                  <Badge key={i} className="bg-purple-100 text-purple-700">{mod}</Badge>
                ))}
              </div>
            </div>

            {pharma.key_drugs?.length > 0 && (
              <div>
                <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <Pill className="w-4 h-4" />
                  Key Products
                </h4>
                <div className="flex flex-wrap gap-2">
                  {pharma.key_drugs?.map((drug, i) => (
                    <Badge key={i} variant="outline">{drug}</Badge>
                  ))}
                </div>
              </div>
            )}

            {pharma.innovation_approach && (
              <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                <h4 className="font-semibold text-indigo-900 mb-1">Innovation Approach</h4>
                <p className="text-indigo-800 text-sm">{pharma.innovation_approach}</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="partnerships" className="space-y-4 pt-4">
            {pharma.recent_acquisitions?.length > 0 && (
              <div>
                <h4 className="font-semibold text-slate-900 mb-3">Recent Acquisitions</h4>
                <div className="flex flex-wrap gap-2">
                  {pharma.recent_acquisitions?.map((acq, i) => (
                    <Badge key={i} className="bg-amber-100 text-amber-700">{acq}</Badge>
                  ))}
                </div>
              </div>
            )}

            {pharma.startup_partnerships?.length > 0 && (
              <div>
                <h4 className="font-semibold text-slate-900 mb-3">Startup Partnerships</h4>
                <div className="space-y-2">
                  {pharma.startup_partnerships?.map((p, i) => (
                    <div key={i} className="p-3 bg-slate-50 rounded-lg">
                      <div className="font-medium text-slate-900">{p.partner_name}</div>
                      <div className="text-sm text-slate-500">
                        {p.partnership_type} {p.year && `(${p.year})`}
                      </div>
                      {p.description && <div className="text-sm text-slate-600 mt-1">{p.description}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(!pharma.startup_partnerships?.length && !pharma.recent_acquisitions?.length) && (
              <div className="text-center py-8 text-slate-500">
                <Handshake className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No partnership data available</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="contact" className="space-y-4 pt-4">
            <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
              <h4 className="font-semibold text-teal-900 mb-2 flex items-center gap-2">
                <Handshake className="w-4 h-4" />
                Best Contact Approach
              </h4>
              <p className="text-teal-800 font-medium">
                {CONTACT_APPROACH_LABELS[pharma.best_contact_approach] || "BD Team"}
              </p>
            </div>

            {pharma.contact_notes && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-semibold text-slate-900 mb-2">Contact Notes</h4>
                <p className="text-slate-600 text-sm">{pharma.contact_notes}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="text-sm text-slate-500 mb-1">Deal Activity</div>
                <div className="font-semibold text-slate-900">{pharma.deal_activity_score || 50}/100</div>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="text-sm text-slate-500 mb-1">Startup Friendly</div>
                <div className="font-semibold text-slate-900">{pharma.startup_friendly_score || 50}/100</div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="outreach" className="space-y-4 pt-4">
            <div className="flex gap-2">
              <Button 
                onClick={() => generateOutreach("bd_team")} 
                disabled={generatingMessage}
                variant="outline"
                className="flex-1"
              >
                {generatingMessage ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Users className="w-4 h-4 mr-2" />}
                Draft for BD Team
              </Button>
              <Button 
                onClick={() => generateOutreach("scientist")} 
                disabled={generatingMessage}
                variant="outline"
                className="flex-1"
              >
                {generatingMessage ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FlaskConical className="w-4 h-4 mr-2" />}
                Draft for Scientists
              </Button>
            </div>

            {outreachMessage && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-slate-900">Generated Message</h4>
                  <Button variant="ghost" size="sm" onClick={copyMessage}>
                    {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                    {copied ? "Copied" : "Copy"}
                  </Button>
                </div>
                <p className="text-slate-700 whitespace-pre-wrap">{outreachMessage}</p>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <div className="flex gap-3 pt-4 border-t mt-4">
          {pharma.website && (
            <Button variant="outline" onClick={() => window.open(pharma.website, '_blank')} className="gap-2">
              <Globe className="w-4 h-4" />
              Website <ExternalLink className="w-3 h-3" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PrepareForPharmaDialog({ open, onClose, data, loading }) {
  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-xl">
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-10 h-10 animate-spin text-indigo-500 mb-4" />
            <p className="text-slate-600">Analyzing your pharma readiness...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  
  if (!data) return null;

  const getScoreColor = (score) => {
    if (score >= 70) return "text-green-600";
    if (score >= 50) return "text-amber-600";
    return "text-red-600";
  };

  const getScoreBgColor = (score) => {
    if (score >= 70) return "bg-green-50 border-green-200";
    if (score >= 50) return "bg-amber-50 border-amber-200";
    return "bg-red-50 border-red-200";
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-indigo-600" />
            Pharma Readiness Assessment
          </DialogTitle>
          <DialogDescription>
            How ready are you to engage with pharma companies?
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Overall Readiness */}
          <div className="p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold">Overall Readiness</h4>
              <span className={`text-2xl font-bold ${getScoreColor(data.overall_readiness)}`}>
                {data.overall_readiness}%
              </span>
            </div>
            <Progress value={data.overall_readiness} className="h-2" />
            <p className="text-sm text-slate-600 mt-2">{data.readiness_summary}</p>
          </div>

          {/* Deal Type Likelihood */}
          <div>
            <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Zap className="w-4 h-4 text-indigo-500" />
              Deal Type Likelihood
            </h4>
            <div className="grid grid-cols-3 gap-3">
              <div className={`p-4 rounded-lg border ${getScoreBgColor(data.licensing_score)}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">Licensing</span>
                  <FileText className="w-4 h-4 text-slate-400" />
                </div>
                <div className={`text-2xl font-bold ${getScoreColor(data.licensing_score)}`}>
                  {data.licensing_score}%
                </div>
                <p className="text-xs text-slate-500 mt-1">IP licensing deals</p>
              </div>
              <div className={`p-4 rounded-lg border ${getScoreBgColor(data.partnership_score)}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">Partnership</span>
                  <Handshake className="w-4 h-4 text-slate-400" />
                </div>
                <div className={`text-2xl font-bold ${getScoreColor(data.partnership_score)}`}>
                  {data.partnership_score}%
                </div>
                <p className="text-xs text-slate-500 mt-1">Co-development</p>
              </div>
              <div className={`p-4 rounded-lg border ${getScoreBgColor(data.ma_score)}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">M&A</span>
                  <Building className="w-4 h-4 text-slate-400" />
                </div>
                <div className={`text-2xl font-bold ${getScoreColor(data.ma_score)}`}>
                  {data.ma_score}%
                </div>
                <p className="text-xs text-slate-500 mt-1">Acquisition target</p>
              </div>
            </div>
          </div>

          {/* Key Questions to Prepare For */}
          <div>
            <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Questions Pharma Will Ask
            </h4>
            <div className="space-y-3">
              {data.questions?.map((q, i) => (
                <div key={i} className="p-4 border rounded-lg bg-white">
                  <div className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-medium flex-shrink-0">
                      {i + 1}
                    </span>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{q.question}</p>
                      <p className="text-sm text-slate-600 mt-1">{q.why_important}</p>
                      {q.suggestion && (
                        <div className="mt-2 p-2 bg-teal-50 rounded text-sm text-teal-800">
                          <span className="font-medium">Tip:</span> {q.suggestion}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Strengths & Gaps */}
          {(data.strengths?.length > 0 || data.gaps?.length > 0) && (
            <div className="grid grid-cols-2 gap-4">
              {data.strengths?.length > 0 && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h5 className="font-medium text-green-900 mb-2">Your Strengths</h5>
                  <ul className="space-y-1">
                    {data.strengths.map((s, i) => (
                      <li key={i} className="text-sm text-green-800 flex items-start gap-2">
                        <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {data.gaps?.length > 0 && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <h5 className="font-medium text-amber-900 mb-2">Gaps to Address</h5>
                  <ul className="space-y-1">
                    {data.gaps.map((g, i) => (
                      <li key={i} className="text-sm text-amber-800 flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                        {g}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PharmaContent() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pharmaList, setPharmaList] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedPharma, setSelectedPharma] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showPrepare, setShowPrepare] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [prepareData, setPrepareData] = useState(null);
  
  // Therapeutic target for matching
  const [therapeuticTarget, setTherapeuticTarget] = useState("");
  
  // Paywall state
  const [hasFullAccess, setHasFullAccess] = useState(true);
  const [paywalled, setPaywalled] = useState(false);
  const [paywalledCount, setPaywalledCount] = useState(0);
  const [showPaywall, setShowPaywall] = useState(false);
  
  // Contribution modal state
  const [showContribute, setShowContribute] = useState(false);

  useEffect(() => {
    fetchPharma();
  }, [fetchPharma]);
  
  // Refetch when therapeutic target changes (with debounce effect)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!loading) {
        fetchPharma();
      }
    }, 500); // Debounce 500ms
    return () => clearTimeout(timer);
  }, [therapeuticTarget, loading, fetchPharma]);

  const fetchPharma = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (therapeuticTarget.trim()) {
        params.append("therapeutic_target", therapeuticTarget.trim());
      }
      const url = `${BACKEND_URL}/api/pharma/matched${params.toString() ? `?${params.toString()}` : ""}`;
      const response = await fetch(url, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setPharmaList(data.pharma_companies || []);
        // Paywall state from API
        setHasFullAccess(data.has_full_access ?? true);
        setPaywalled(data.paywalled ?? false);
        setPaywalledCount(data.paywalled_count ?? 0);
      }
    } catch (error) {
      console.error("Failed to fetch pharma:", error);
    } finally {
      setLoading(false);
    }
  }, [therapeuticTarget]);

  const handlePrepareForPharma = async () => {
    setPreparing(true);
    setShowPrepare(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/pharma/prepare`, {
        method: "POST",
        credentials: "include"
      });
      
      if (response.ok) {
        const data = await response.json();
        setPrepareData(data);
      } else {
        toast.error("Failed to generate readiness assessment");
      }
    } catch (error) {
      toast.error("Failed to generate readiness assessment");
    } finally {
      setPreparing(false);
    }
  };

  const filteredPharma = pharmaList.filter(p => {
    if (!search) return true;
    const s = search.toLowerCase();
    return p.name?.toLowerCase().includes(s) ||
           p.indication_focus?.some(ind => ind.toLowerCase().includes(s)) ||
           p.headquarters?.toLowerCase().includes(s);
  });

  const handleSelect = (pharma) => {
    setSelectedPharma(pharma);
    setShowDetail(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto" data-testid="pharma-page">
      {/* Header */}
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Building className="w-5 h-5 text-white" />
            </div>
            Pharma Partners
          </h1>
          <p className="text-slate-600 mt-1">
            Discover pharma companies aligned with your therapeutic focus
          </p>
        </div>
        <Button onClick={handlePrepareForPharma} variant="outline" className="gap-2" data-testid="prepare-for-pharma">
          <Shield className="w-4 h-4" />
          Prepare for Pharma
        </Button>
      </div>

      {/* Therapeutic Target Input (optional) */}
      <div className="mb-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <FlaskConical className="w-4 h-4 text-indigo-600" />
          <span className="font-medium text-indigo-900">Therapeutic Target</span>
          <span className="text-xs text-indigo-500">(optional)</span>
        </div>
        <p className="text-sm text-indigo-700 mb-3">
          Enter a specific target (e.g., HER2, KRAS, PD-1) to boost matches for pharma companies working in that area.
        </p>
        <Input
          placeholder="e.g., HER2, KRAS, PD-1, CD19..."
          value={therapeuticTarget}
          onChange={(e) => setTherapeuticTarget(e.target.value)}
          className="max-w-md bg-white"
          data-testid="therapeutic-target-input"
        />
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by name, indication, or location..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Results */}
      {filteredPharma.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-12 text-center">
            <Building className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-medium text-slate-900 mb-2">No pharma matches found</h3>
            <p className="text-slate-500 mb-4">Complete your company profile to get personalized matches</p>
            <Button onClick={() => navigate("/profile")}>Complete Profile</Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="mb-4 flex items-center justify-between">
            <span className="text-sm text-slate-500">
              {hasFullAccess ? (
                <>Showing {filteredPharma.length} pharma companies ranked by match score</>
              ) : (
                <>Top 3 shown • {paywalledCount}+ more matches available</>
              )}
            </span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowContribute(true)}
              className="text-indigo-600 border-indigo-300"
            >
              <Lightbulb className="w-4 h-4 mr-1" />
              Share Intel
            </Button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filteredPharma.map((pharma) => (
              <PharmaCard 
                key={pharma.pharma_id} 
                pharma={pharma} 
                onSelect={handleSelect}
              />
            ))}
          </div>
          
          {/* Paywall Banner */}
          {paywalled && (
            <div className="mt-6 p-6 bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl text-white text-center">
              <Lock className="w-8 h-8 mx-auto mb-3 text-amber-400" />
              <h3 className="text-xl font-bold mb-2">
                {paywalledCount}+ More Pharma Matches Waiting
              </h3>
              <p className="text-slate-300 mb-4 max-w-md mx-auto">
                Unlock full access to all pharma company matches and BD intelligence.
              </p>
              <Button 
                onClick={() => setShowPaywall(true)}
                className="bg-indigo-500 hover:bg-indigo-600 text-white"
              >
                Unlock Full Access
              </Button>
            </div>
          )}
        </>
      )}

      {/* Detail Dialog */}
      <PharmaDetailDialog
        pharma={selectedPharma}
        open={showDetail}
        onClose={() => setShowDetail(false)}
      />

      {/* Prepare for Pharma Dialog */}
      <PrepareForPharmaDialog
        open={showPrepare}
        onClose={() => setShowPrepare(false)}
        data={prepareData}
        loading={preparing}
      />
      
      {/* Paywall Modal */}
      <PaywallModal
        open={showPaywall}
        onClose={() => setShowPaywall(false)}
        paywallCount={paywalledCount}
        category="pharma companies"
        onSubscribe={() => setShowPaywall(false)}
        onPromoApplied={() => { setShowPaywall(false); fetchPharma(); }}
      />
      
      {/* Contribution Modal */}
      <ContributionModal
        open={showContribute}
        onClose={() => setShowContribute(false)}
        category="pharma"
        onSuccess={() => toast.success("Thank you for your contribution!")}
      />
    </div>
  );
}

export default function Pharma() {
  return (
    <AppLayout>
      {() => <PharmaContent />}
    </AppLayout>
  );
}
